/**
 * useBuildingData - Custom Hook for Building Data Management
 *
 * Performance (Option A):
 * - Split loading: buildingLoading unblocks the UI; dropsLoading tracks background drops fetch
 * - Parallel getDoc(building) + getDoc(private_data) saves one network round-trip
 * - Completion % derived from drops data (no separate getCountFromServer query)
 * - Initial drops fetch starts inside loadBuildingData (no effect scheduling gap)
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getFirestore, doc, getDoc, collection, where, query, orderBy, limit, getDocs } from "firebase/firestore";
import { firebaseApp } from "../firebase/config";
import { buildingListActions } from "../store/building-slice";
import {
  getBuildingScreenErrorMessage,
  validateBuildingData,
} from "../utils/buildingErrorHandling";
import {
  FIREBASE_COLLECTIONS,
  FIREBASE_SUBCOLLECTIONS,
  FORM_TYPES,
  FORM_ACCESS_TYPES,
  CLEAN_STATUS,
} from "../constants/firebase";

/**
 * Derives official-clean completion % from a drops array (replaces getCountFromServer).
 */
const computeCleanPercentageCompleted = (dropsData, noDrops) => {
  const cleanedCount = (dropsData || []).filter(
    (drop) => drop.cleanStatus === CLEAN_STATUS.CLEANED
  ).length;
  const totalDrops = noDrops || dropsData?.length || 1;
  return Number((cleanedCount / totalDrops).toFixed(2));
};

const formatDrop = (drop) => {
  try {
    const dropData = drop.data();
    return {
      ...dropData,
      id: drop.id,
      singleWindows: dropData.singleWindows || [],
      currentDate: dropData.date
        ? new Date(dropData.date.toDate()).toLocaleDateString("en-GB")
        : "",
    };
  } catch (err) {
    console.error("Error formatting drop:", err);
    return null;
  }
};

/**
 * Fetches drops whenever Redux currentClean changes.
 * Mount this on any screen where the user can change the selected clean
 * (e.g. CleanAndDropSelectionScreen). BuildingScreen may be frozen while
 * that screen is active, so its copy of this hook will not re-run.
 *
 * @param {Object} selectedBuilding - Selected building object with isAdhoc flag
 * @param {Object} options
 * @param {string} [options.officialClean] - Firestore official clean (for progress %)
 * @param {number} [options.noDrops] - Total drop count from building data
 * @param {React.MutableRefObject<string|null>} [options.skipInitialCleanRef]
 *   When set to a clean id by loadBuildingData, skips one effect run for that id
 *   to avoid double-fetching on initial load.
 */
export const useDropsOnCleanChange = (
  selectedBuilding,
  { officialClean, noDrops, skipInitialCleanRef } = {}
) => {
  const dispatch = useDispatch();
  const [dropsLoading, setDropsLoading] = useState(false);
  const [error, setError] = useState(null);
  const currentClean = useSelector((state) => state.buildingList.currentClean);
  const buildingData = useSelector((state) => state.buildingList.buildingData);

  const db = getFirestore(firebaseApp);
  const dropsControllerRef = useRef(null);
  const lastFetchedCleanRef = useRef(null);

  const resolvedOfficialClean = officialClean ?? buildingData?.currentClean;
  const resolvedNoDrops = noDrops ?? buildingData?.noDrops;
  const buildingId =
    selectedBuilding?.site_id || selectedBuilding?.site_name || null;

  // Refs keep loadDropsForClean stable when buildingData arrives so the parent
  // loadBuildingData callback is not recreated (which would re-trigger its effect).
  const officialCleanRef = useRef(resolvedOfficialClean);
  const noDropsRef = useRef(resolvedNoDrops);
  officialCleanRef.current = resolvedOfficialClean;
  noDropsRef.current = resolvedNoDrops;

  const getBuildingDrops = useCallback(
    async (year, clean, signal) => {
      if (!clean || !buildingId) {
        console.warn("No clean cycle or building id provided to getBuildingDrops");
        return [];
      }

      try {
        const buildingRef = doc(db, FIREBASE_COLLECTIONS.BUILDINGS, buildingId);
        const dropsRef = collection(buildingRef, year, clean, FIREBASE_SUBCOLLECTIONS.DROPS);
        const dropsSnapshot = await getDocs(dropsRef);

        if (signal?.aborted) return [];

        return dropsSnapshot.docs
          .map(formatDrop)
          .filter((drop) => drop !== null)
          .sort((a, b) => (a.num > b.num ? 1 : -1));
      } catch (err) {
        if (signal?.aborted) return [];
        console.error("Error fetching drops:", err);
        throw err;
      }
    },
    [db, buildingId]
  );

  const loadDropsForClean = useCallback(
    async (cleanId) => {
      if (!selectedBuilding || selectedBuilding.isAdhoc || !cleanId) {
        return;
      }

      if (dropsControllerRef.current) {
        try {
          dropsControllerRef.current.abort();
        } catch (abortError) {
          console.warn("Error aborting previous drops request:", abortError);
        }
      }

      const controller = new AbortController();
      dropsControllerRef.current = controller;
      const signal = controller.signal;

      try {
        setDropsLoading(true);

        if (
          lastFetchedCleanRef.current &&
          lastFetchedCleanRef.current !== cleanId
        ) {
          dispatch(buildingListActions.setDrops({ drops: [] }));
        }

        const dropsData = await getBuildingDrops(
          FIREBASE_SUBCOLLECTIONS.CURRENT_YEAR,
          cleanId,
          signal
        );

        if (signal.aborted) return;

        lastFetchedCleanRef.current = cleanId;
        dispatch(buildingListActions.setDrops({ drops: dropsData || [] }));

        if (cleanId === officialCleanRef.current) {
          dispatch(
            buildingListActions.setLastDropCleanName({
              currentCleanPercentageCompleted: computeCleanPercentageCompleted(
                dropsData,
                noDropsRef.current
              ),
            })
          );
        }
      } catch (err) {
        if (signal.aborted) return;

        console.error("Error loading drops:", err);
        const friendlyError = getBuildingScreenErrorMessage(err);
        setError(friendlyError);
        dispatch(buildingListActions.setError({ error: friendlyError }));
      } finally {
        if (!signal.aborted) {
          setDropsLoading(false);
        }
      }
    },
    [selectedBuilding, getBuildingDrops, dispatch]
  );

  useEffect(() => {
    if (!buildingId || !selectedBuilding || selectedBuilding.isAdhoc || !currentClean) {
      return;
    }

    if (skipInitialCleanRef?.current === currentClean) {
      skipInitialCleanRef.current = null;
      lastFetchedCleanRef.current = currentClean;
      return;
    }

    if (lastFetchedCleanRef.current === currentClean) {
      return;
    }

    loadDropsForClean(currentClean);

    return () => {
      if (dropsControllerRef.current) {
        try {
          dropsControllerRef.current.abort();
        } catch (abortError) {
          console.warn("Error aborting drops controller on cleanup:", abortError);
        }
        dropsControllerRef.current = null;
      }
    };
  }, [buildingId, selectedBuilding, currentClean, loadDropsForClean, skipInitialCleanRef]);

  const clearError = useCallback(() => {
    setError(null);
    dispatch(buildingListActions.setError({ error: null }));
  }, [dispatch]);

  return { dropsLoading, error, clearError, loadDropsForClean };
};

/**
 * Hook that manages all building data fetching with proper error handling
 * and request cancellation
 *
 * currentClean handling:
 * - data.currentClean (Firestore) — official clean; used for today's-form checks
 *   and currentCleanPercentageCompleted (derived from official-clean drops).
 * - Redux currentClean — user-selected clean in the UI; takes priority when
 *   fetching drops via getBuildingDrops.
 *
 * @param {Object} selectedBuilding - Selected building object with isAdhoc flag
 * @param {number} refreshVersion - Version number to trigger refreshes
 * @returns {Object} Hook state and helpers
 */
export const useBuildingData = (selectedBuilding, refreshVersion) => {
  const dispatch = useDispatch();
  const [buildingLoading, setBuildingLoading] = useState(false);
  const [error, setError] = useState(null);

  // Redux state — currentClean here reflects the user's UI selection
  const buildingData = useSelector((state) => state.buildingList.buildingData);
  const currentClean = useSelector((state) => state.buildingList.currentClean);
  const drops = useSelector((state) => state.buildingList.drops);

  const db = getFirestore(firebaseApp);
  const buildingControllerRef = useRef(null);
  const skipInitialCleanRef = useRef(null);

  const {
    dropsLoading,
    error: dropsError,
    loadDropsForClean,
  } = useDropsOnCleanChange(selectedBuilding, { skipInitialCleanRef });

  const loadDropsForCleanRef = useRef(loadDropsForClean);
  loadDropsForCleanRef.current = loadDropsForClean;

  /**
   * Checks if a specific form exists for today.
   * Uses data.currentClean (official Firestore clean) — passed in by getBuildingData.
   */
  const checkIfTodaysFormExist = useCallback(
    async (formType, clean, signal) => {
      try {
        const buildingRef = doc(
          db,
          FIREBASE_COLLECTIONS.BUILDINGS,
          selectedBuilding.site_id || selectedBuilding.site_name
        );

        const formsRef = collection(buildingRef, FIREBASE_SUBCOLLECTIONS.FORMS);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let q;
        if (formType === FORM_TYPES.EQUIPMENT) {
          q = query(
            formsRef,
            where("type", "==", formType),
            where("accessFormType", "==", FORM_ACCESS_TYPES.PRE),
            where("date", ">", today),
            orderBy("date", "desc"),
            limit(1)
          );
        } else {
          q = query(
            formsRef,
            where("type", "==", formType),
            where("date", ">", today),
            orderBy("date", "desc"),
            limit(1)
          );
        }

        const result = await getDocs(q);

        if (signal?.aborted) return null;

        if (result.empty) {
          return null;
        }

        let formData;
        result.forEach((d) => {
          formData = d.data();
        });

        return formData || null;
      } catch (err) {
        if (signal?.aborted) return null;
        console.error(`Error checking for ${formType} form:`, err);
        return null;
      }
    },
    [db, selectedBuilding]
  );

  /**
   * Fetches regular (non-adhoc) building data.
   * Uses data.currentClean (Firestore) for form checks only.
   * Completion % is derived later from drops (see loadDropsForCurrentClean).
   */
  const getBuildingData = useCallback(
    async (signal) => {
      try {
        const buildingId = selectedBuilding.site_id || selectedBuilding.site_name;
        const buildingRef = doc(db, FIREBASE_COLLECTIONS.BUILDINGS, buildingId);
        const privateDataRef = doc(
          buildingRef,
          FIREBASE_SUBCOLLECTIONS.PRIVATE_DATA,
          "private"
        );

        // Performance: fetch building doc and private address in parallel (saves one RTT)
        const [buildingSnapshot, addressSnapshot] = await Promise.all([
          getDoc(buildingRef),
          getDoc(privateDataRef),
        ]);

        if (signal?.aborted) return null;

        if (!buildingSnapshot.exists()) {
          throw new Error("Building data not found");
        }

        let data = buildingSnapshot.data();
        data.id = buildingSnapshot.id;

        if (data.lastCleaned) {
          try {
            data.lastCleaned = new Date(data.lastCleaned.toDate()).toUTCString();
          } catch (err) {
            console.warn("Error formatting lastCleaned date:", err);
            data.lastCleaned = "";
          }
        }

        if (data.currentCleanRef) {
          try {
            data.currentCleanData = (await getDoc(data.currentCleanRef)).data();
          } catch (err) {
            console.warn("Error fetching current clean data:", err);
            data.currentCleanData = null;
          }
        }

        if (addressSnapshot.exists()) {
          data.address = addressSnapshot.data().address || "";
        }

        if (signal?.aborted) return null;

        const officialCurrentClean = data.currentClean;

        const [weatherForm, equipmentForm, ppeForm] = await Promise.all([
          checkIfTodaysFormExist(FORM_TYPES.WEATHER, officialCurrentClean, signal),
          checkIfTodaysFormExist(FORM_TYPES.EQUIPMENT, officialCurrentClean, signal),
          checkIfTodaysFormExist("PPE", officialCurrentClean, signal),
        ]);

        if (signal?.aborted) return null;

        data.weatherForm = weatherForm;
        data.equipmentForm = equipmentForm;
        data.ppeForm = ppeForm;

        return validateBuildingData(data);
      } catch (err) {
        if (signal?.aborted) return null;
        throw err;
      }
    },
    [db, selectedBuilding, checkIfTodaysFormExist]
  );

  const getAdhocSiteData = useCallback(
    async (signal) => {
      try {
        const adhocSiteRef = doc(
          db,
          FIREBASE_COLLECTIONS.ADHOC_SITES,
          selectedBuilding.site_id || selectedBuilding.site_name
        );
        const adhocSiteSnapshot = await getDoc(adhocSiteRef);

        if (signal?.aborted) return null;

        if (!adhocSiteSnapshot.exists()) {
          throw new Error("Adhoc site not found");
        }

        const data = adhocSiteSnapshot.data();
        data.id = adhocSiteSnapshot.id;
        data.isAdhoc = true;

        return validateBuildingData(data);
      } catch (err) {
        if (signal?.aborted) return null;
        throw err;
      }
    },
    [db, selectedBuilding]
  );

  const cleanupBuildingState = useCallback(() => {
    dispatch(
      buildingListActions.setBuilding({
        name: "",
        data: null,
        currentClean: null,
        currentCleanData: null,
        drops: null,
      })
    );
    dispatch(buildingListActions.setWeatherForm({ weatherForm: false }));
    dispatch(buildingListActions.setCurrentDrop({ drop: null }));
    dispatch(buildingListActions.setLatestWeatherInfo({ latestWeather: null }));
    dispatch(buildingListActions.setEquipmentForm({ equipmentForm: false }));
    dispatch(buildingListActions.setPpeForm({ ppeForm: false }));
  }, [dispatch]);

  /**
   * Loads the building document, unblocks the UI immediately, then kicks off
   * the initial drops fetch without waiting for a useEffect cycle.
   */
  const loadBuildingData = useCallback(async () => {
    if (!selectedBuilding) return;

    if (buildingControllerRef.current) {
      try {
        buildingControllerRef.current.abort();
      } catch (abortError) {
        console.warn("Error aborting previous building request:", abortError);
      }
    }

    const controller = new AbortController();
    buildingControllerRef.current = controller;
    const signal = controller.signal;

    let loadedData = null;

    try {
      setBuildingLoading(true);
      setError(null);

      let data;
      if (selectedBuilding.isAdhoc) {
        data = await getAdhocSiteData(signal);
      } else {
        data = await getBuildingData(signal);
      }

      if (signal.aborted) {
        return;
      }

      if (!data) {
        throw new Error("Failed to load building data");
      }

      loadedData = data;

      dispatch(
        buildingListActions.setBuilding({
          name: selectedBuilding,
          data,
          currentClean: data.currentClean,
          currentCleanData: data.currentCleanData,
          drops: null,
        })
      );
    } catch (err) {
      if (signal.aborted) {
        return;
      }
      console.error("Error loading building data:", err);
      const friendlyError = getBuildingScreenErrorMessage(err);
      setError(friendlyError);
      dispatch(buildingListActions.setError({ error: friendlyError }));
    } finally {
      if (!signal.aborted) {
        setBuildingLoading(false);
      }
    }

    // Start initial drops fetch after UI is unblocked (avoids effect scheduling gap)
    if (
      loadedData?.currentClean &&
      !selectedBuilding.isAdhoc &&
      !signal.aborted
    ) {
      skipInitialCleanRef.current = loadedData.currentClean;
      loadDropsForCleanRef.current(loadedData.currentClean);
    }
  }, [selectedBuilding, dispatch, getBuildingData, getAdhocSiteData]);

  useEffect(() => {
    if (!selectedBuilding) return;

    loadBuildingData();

    return () => {
      skipInitialCleanRef.current = null;

      if (buildingControllerRef.current) {
        try {
          buildingControllerRef.current.abort();
        } catch (abortError) {
          console.warn("Error aborting building controller on cleanup:", abortError);
        }
        buildingControllerRef.current = null;
      }
      cleanupBuildingState();
    };
  }, [selectedBuilding, refreshVersion, loadBuildingData, cleanupBuildingState]);

  const reload = useCallback(() => {
    loadBuildingData();
  }, [loadBuildingData]);

  const clearError = useCallback(() => {
    setError(null);
    dispatch(buildingListActions.setError({ error: null }));
  }, [dispatch]);

  const combinedError = error || dropsError;

  return {
    loading: buildingLoading,
    buildingLoading,
    dropsLoading,
    error: combinedError,
    buildingData,
    currentClean,
    drops,
    clearError,
    reload,
  };
};